<template>
  <!--
    A `<label>` wrapping a real `<input type="radio">`: the whole card stays
    clickable exactly as before, but it is now reachable with Tab, selectable
    with Space, and navigable with the arrow keys within its group, and screen
    readers announce it as a radio with its checked state. The `--selected`
    class is kept purely for styling; the input's `:checked` is the source of
    truth that assistive technology reads.
  -->
  <label class="radio-card" :class="classNames">
    <div class="radio-card__input">
      <input
        class="radio-card__radio"
        type="radio"
        :name="groupName"
        :value="value"
        :checked="selected"
        :disabled="disabled"
        @change="select(value)"
      />
      <span class="radio-card__radio-control"></span>
    </div>
    <div class="radio-card__content">
      <div class="radio-card__labels">
        <span class="radio-card__label">{{ label }}</span>
        <div v-if="sideLabel">
          <Badge :rounded="true" :small="true">{{ sideLabel }}</Badge>
        </div>
      </div>
      <div v-if="hasSlot" class="radio-card__description">
        <slot></slot>
      </div>
    </div>
  </label>
</template>

<script>
export default {
  name: 'RadioCard',
  props: {
    value: {
      type: [String, Number, Boolean, Object],
      required: false,
      default: '',
    },
    modelValue: {
      type: [String, Number, Boolean, Object],
      required: false,
      default: '',
    },
    label: {
      type: String,
      required: true,
    },
    sideLabel: {
      type: String,
      required: false,
      default: undefined,
    },
    // Radios only behave as one group -- and only support arrow-key navigation
    // between their members -- when they share a `name`. Cards bound to one
    // `v-model` are rendered by a single parent, so the name defaults to that
    // parent's uid, which groups siblings without any caller having to opt in.
    // Pass it explicitly if one parent renders several independent groups.
    name: {
      type: String,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['input', 'update:modelValue'],
  computed: {
    classNames() {
      return {
        'radio-card--selected': this.modelValue === this.value,
        'radio-card--disabled': this.disabled,
      }
    },
    selected() {
      return this.modelValue === this.value
    },
    groupName() {
      return this.name ?? `radio-card-${this.$parent?.$?.uid ?? this.$?.uid}`
    },
    hasSlot() {
      return !!this.$slots.default
    },
  },
  methods: {
    select(value) {
      if (this.disabled || this.selected) {
        return
      }
      this.$emit('input', value)
      this.$emit('update:modelValue', value)
    },
  },
}
</script>
